import {
  ViolationReturn,
  WorkOrderReturn,
} from '../../clients/rust-api-client';

/**
 * The non-comment rows of a violation's activity timeline.
 *
 * **Only what the record itself can vouch for.** The backend keeps no
 * history of changes (#609): an update overwrites the row, leaving
 * `updated_by`/`date_updated` as the *last* writer and nothing before it.
 * So these are the moments a violation stores a timestamp for — created,
 * notice sent, response received, resolved — plus the most recent edit,
 * labelled as exactly that. Inventing a "changed status" row from the
 * current status would be a history we do not have.
 */
export type ActivityEventKind =
  | 'created'
  | 'notified'
  | 'responded'
  | 'resolved'
  | 'completed'
  | 'edited';

export interface ActivityEvent {
  id: string;
  kind: ActivityEventKind;
  at: string;
  /** A management user's id, when the record names one. */
  actorId: string | null;
  /** What happened, without the actor: "created this violation". */
  text: string;
}

/**
 * `resolved_by` (#116) is served by the backend but not yet in the
 * generated client, which is regenerated only after the backend deploys.
 * Drop this once `ViolationReturn` carries the field.
 */
type ViolationWithResolver = ViolationReturn & { resolved_by?: string | null };

const time = (value: string): number => {
  const t = Date.parse(value);
  return Number.isNaN(t) ? 0 : t;
};

/** An edit within a minute of creation is the creation, not a change. */
const SAME_MOMENT_MS = 60_000;

export function violationEvents(v: ViolationReturn): ActivityEvent[] {
  const events: ActivityEvent[] = [];
  const createdAt = v.date_created ?? v.date_of_violation;

  events.push({
    id: 'created',
    kind: 'created',
    at: createdAt,
    actorId: v.created_by || null,
    text: 'created this violation',
  });

  if (v.notification_sent_date) {
    events.push({
      id: 'notified',
      kind: 'notified',
      at: v.notification_sent_date,
      actorId: null,
      text: 'Notice sent to the homeowner',
    });
  }

  if (v.response_received_date) {
    events.push({
      id: 'responded',
      kind: 'responded',
      at: v.response_received_date,
      actorId: null,
      text: 'The homeowner responded',
    });
  }

  if (v.resolution_date) {
    // Violations closed before #116 recorded nobody, and say only that.
    const resolvedBy = (v as ViolationWithResolver).resolved_by ?? null;
    events.push({
      id: 'resolved',
      kind: 'resolved',
      at: v.resolution_date,
      actorId: resolvedBy,
      text: resolvedBy ? 'resolved this violation' : 'Marked resolved',
    });
  }

  if (v.date_updated && time(v.date_updated) - time(createdAt) > SAME_MOMENT_MS) {
    events.push({
      id: 'edited',
      kind: 'edited',
      at: v.date_updated,
      actorId: v.updated_by ?? null,
      text: 'made the most recent edit',
    });
  }

  return events.sort((a, b) => time(a.at) - time(b.at));
}

/**
 * The same rule for a work order: created, completed, and the most recent
 * edit. Work orders keep no change history, so these plus its comments
 * (#616) are the whole of what their timeline can truthfully show. A
 * scheduled date is a plan, not something that happened, so it is not an
 * event.
 */
export function workOrderEvents(w: WorkOrderReturn): ActivityEvent[] {
  const events: ActivityEvent[] = [];
  const createdAt = w.date_created;

  if (createdAt) {
    events.push({
      id: 'created',
      kind: 'created',
      at: createdAt,
      actorId: w.created_by || null,
      text: 'created this work order',
    });
  }

  if (w.completion_date) {
    events.push({
      id: 'completed',
      kind: 'completed',
      at: w.completion_date,
      actorId: null,
      text: 'Marked completed',
    });
  }

  if (
    w.date_updated &&
    (!createdAt || time(w.date_updated) - time(createdAt) > SAME_MOMENT_MS)
  ) {
    events.push({
      id: 'edited',
      kind: 'edited',
      at: w.date_updated,
      actorId: w.updated_by ?? null,
      text: 'made the most recent edit',
    });
  }

  return events.sort((a, b) => time(a.at) - time(b.at));
}

/** Oldest first; ties keep their order. */
export const byTime = <T extends { at: string }>(items: T[]): T[] =>
  items
    .map((item, i) => ({ item, i }))
    .sort((a, b) => time(a.item.at) - time(b.item.at) || a.i - b.i)
    .map(({ item }) => item);
